import { Popover } from "@base-ui-components/react";
import clsx from "clsx";
import { useMemo } from "react";
import { Button } from "src/components/Button/Button";
import { Icon } from "src/components/Icon";
import { Loading } from "src/components/Loading/Loading";
import type { Merge } from "src/models/utility";
import { useDropdownContext } from "../root/Dropdown.root";
import styling from "./Dropdown.trigger.module.scss";

export namespace DropdownTrigger {
	export type ElementName = "button";
	export type ElementType = HTMLButtonElement;
	export type Props = Merge<
		Omit<Button.Props, "ref">,
		{
			useChevrons?: boolean;
		}
	>;
}

export const DropdownTrigger = (props: DropdownTrigger.Props) => {
	const {
		children,
		className,
		color = "neutral",
		variant = "bordered",
		leftIcon,
		useChevrons = false,
		rightIcon,
		...rest
	} = props;

	const { open, toggle, state = "default" } = useDropdownContext();

	const _getRightIcon = useMemo(() => {
		if (state === "default") {
			if (useChevrons) {
				return open ? (
					<Icon name="SmallChevronUp" size="small" />
				) : (
					<Icon name="SmallChevronDown" size="small" />
				);
			}
			return rightIcon;
		}
		return undefined;
	}, [open, rightIcon, state, useChevrons]);

	const _renderChildrenContent = () => {
		if (state === "default" && children) {
			return children;
		}
		if (state === "loading") {
			return <Loading size="small" />;
		}
		return null;
	};

	const _isDisabled = () => {
		return state === "disabled" || state !== "default" ? "disabled" : state;
	};

	return (
		<Popover.Trigger
			render={({ key, ...triggerProps } : any) => {
				const triggerPropsWithKey = triggerProps as typeof triggerProps & { key?: React.Key };
				return (
					<Button
						key={key ?? triggerPropsWithKey.key}
						{...triggerProps}
						aria-haspopup="menu"
						aria-expanded={open}
						leftIcon={state === "default" ? leftIcon : undefined}
						color={color}
						variant={variant}
						className={clsx(styling.button, {
							[styling["button--loading"]]: state === "loading",
							[styling["button--classic"]]:
								_isDisabled() !== "disabled" && variant === "bordered" && color === "neutral",
							[`${className}`]: className,
						})}
						rightIcon={_getRightIcon}
						onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
							rest.onClick?.(e);
							if (!e.defaultPrevented) toggle();
						}}
						{...rest}
					>
						{_renderChildrenContent()}
					</Button>
				);
			}}
		/>
	);
};
